import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import { Check, ChevronDown, Search } from 'lucide-react';

interface ModelReferenceSelectProps {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  label: string;
  placeholder: string;
  emptyLabel: string;
  testId?: string;
}

/** Selector de modelo con búsqueda (combobox accesible; teclado + ratón). */
export function ModelReferenceSelect({
  options,
  value,
  onChange,
  label,
  placeholder,
  emptyLabel,
  testId,
}: ModelReferenceSelectProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const listId = useId();

  const unique = useMemo(
    () => Array.from(new Set(options)).sort((a, b) => a.localeCompare(b)),
    [options],
  );
  const filtered = useMemo(() => {
    const term = query.trim().toLowerCase();
    const matched = term
      ? unique.filter((option) => option.toLowerCase().includes(term))
      : unique;
    return matched.slice(0, 100);
  }, [unique, query]);

  useEffect(() => {
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, []);

  useEffect(() => {
    setHighlight(0);
  }, [query, open]);

  const choose = (option: string) => {
    onChange(option);
    setOpen(false);
    setQuery('');
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setHighlight((current) => Math.min(current + 1, filtered.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setHighlight((current) => Math.max(current - 1, 0));
    } else if (event.key === 'Enter' && open && filtered[highlight]) {
      event.preventDefault();
      choose(filtered[highlight]);
    } else if (event.key === 'Escape') {
      setOpen(false);
    }
  };

  return (
    <div ref={rootRef} className="relative mt-1">
      <div className="flex items-center gap-1 rounded border bg-background px-2 py-1">
        <Search className="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden="true" />
        <input
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-label={label}
          className="w-full bg-transparent text-sm outline-none"
          value={open ? query : value}
          placeholder={placeholder}
          onFocus={() => {
            setOpen(true);
            setQuery('');
          }}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onKeyDown={onKeyDown}
          data-testid={testId}
        />
        <button
          type="button"
          aria-label={label}
          aria-expanded={open}
          className="shrink-0 text-gray-400"
          onClick={() => setOpen((current) => !current)}
        >
          <ChevronDown className="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
      {open && (
        <ul
          id={listId}
          role="listbox"
          className="absolute z-40 mt-1 max-h-60 w-full overflow-auto rounded-md border bg-card p-1 shadow-lg"
        >
          {filtered.length === 0 ? (
            <li className="px-2 py-1 text-xs text-gray-500">{emptyLabel}</li>
          ) : (
            filtered.map((option, index) => (
              <li
                key={option}
                role="option"
                aria-selected={option === value}
                className={`flex cursor-pointer items-center justify-between gap-2 rounded px-2 py-1 text-sm ${
                  index === highlight ? 'bg-accent' : ''
                }`}
                onMouseEnter={() => setHighlight(index)}
                onMouseDown={(event) => {
                  event.preventDefault();
                  choose(option);
                }}
              >
                <span className="truncate">{option}</span>
                {option === value && (
                  <Check className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
                )}
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
